import React, { useEffect, useMemo, useState } from "react";
import { useTranslate } from "../../translate";
import Button from "../button/Button";
import classNames from "classnames";
import Tooltip from "../tooltip/Tooltip";
import { useBreakpointsContext } from "../../hooks";
import AtlasCardItinerary from "./AtlasCardItinerary";
import AtlasCardFooter from "./AtlasCardFooter";
import { useItineraryTeasers } from "../explorer/hooks";

function AtlasCard(props) {
    const translate = useTranslate();
    const { isMobile, isTablet } = useBreakpointsContext();
    const {
        itinerariesUrls,
        children,
        showOrderRight,
        showOrder,
        order,
        className,
        isActive,
        description,
        title,
        address,
        website,
        phone,
        coords,
        img,
        businessType,
        onFavoriteBtnClick,
        onMapBtnClick,
        isFavorite,
        stopId,
    } = props;
    const tooltipLabel = isFavorite ? "Remove from favorites" : "Add to favorites";
    const [showItineraries, setShowItineraries] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const hasItinerariesUrls = itinerariesUrls && itinerariesUrls.length > 0;
    const itineraryIds = useMemo(() => {
        return itinerariesUrls.map(url => {
            // HACK: get itineraryId from url
            // url e.g.: "https://dev.tripper.app/api/v2.1/itineraries/14763?_format=json"
            const strs = url.split("/");
            return strs[strs.length - 1].split("?")[0];
        });
    }, [itinerariesUrls]);
    const initialize = !showItineraries || itinerariesUrls.length === 0 || hasLoaded;
    const [itineraries, isLoading] = useItineraryTeasers(itineraryIds, initialize);

    useEffect(() => {
        if (isLoading && !hasLoaded) {
            setHasLoaded(true);
        }
        //eslint-disable-next-line
    }, [isLoading]);

    return (
        <article className={classNames(["trpr-atlas-card", className])} data-stop-number={order} data-stop-id={stopId}>
            <div className="trpr-atlas-card__wrapper">
                <figure className="trpr-atlas-card__figure">
                    <img src={img} alt={title} />
                    <span className="trpr-atlas-card__business-type">{businessType}</span>
                    {showOrder && (
                        <span className={classNames(["trpr-atlas-card__order", { "is-active": isActive }])}>
                            {order}
                        </span>
                    )}
                </figure>
                <div className="trpr-atlas-card__toolbar">
                    {showOrderRight && <span className="trpr-atlas-card__order-right">{order}</span>}
                    {!showOrderRight && (
                        <div className="trpr__tooltip-primary-wrapper">
                            <Button
                                onClick={() => onFavoriteBtnClick(stopId)}
                                className={classNames(["trpr-atlas-card__button", { "is-favorite": isFavorite }])}
                                type="icon"
                                icon="star"
                                size="30"
                            />
                            <Tooltip className="is-floating trpr__mobile-alt is-right">{tooltipLabel}</Tooltip>
                        </div>
                    )}
                    {(isMobile || isTablet) && (
                        <div className="trpr__tooltip-primary-wrapper">
                            <Button
                                onClick={() => onMapBtnClick(stopId)}
                                className="trpr-atlas-card__button"
                                type="icon"
                                icon="marker"
                                size="30"
                            />
                            <Tooltip className="is-floating trpr__mobile-alt is-right">Show on map</Tooltip>
                        </div>
                    )}
                </div>
                <div className="trpr-atlas-card__header">
                    <h3>{title}</h3>
                    <p>{address}</p>
                </div>
                {description && (
                    <div className="trpr-atlas-card__description" dangerouslySetInnerHTML={{ __html: description }} />
                )}
                <AtlasCardFooter phone={phone} website={website} coords={coords} />
                {hasItinerariesUrls && (
                    <div className="trpr-atlas-card__bottom">
                        <button
                            onClick={() => setShowItineraries(!showItineraries)}
                            type="button"
                            className={classNames("trpr-atlas-card__btn", {
                                "is-active": showItineraries,
                            })}
                        >
                            <i className="trpr__icon-triangle"></i>
                            <span>{showItineraries ? "Hide" : "Show"} itineraries that feature this stop</span>
                        </button>
                    </div>
                )}
            </div>
            <div className="trpr-atlas-card__itineraries">
                {isLoading && showItineraries && <div className="trpr-atlas-card__itineraries-loading">Loading...</div>}
                {!isLoading &&
                    showItineraries &&
                    itineraries.map(itinerary => {
                        return (
                            <AtlasCardItinerary
                                days={itinerary.days}
                                distance={itinerary.distance}
                                id={itinerary.id}
                                key={itinerary.id}
                                stops={itinerary.totalStops}
                                title={translate(itinerary, "name")}
                                travelType={translate(itinerary, "type.name")}
                            />
                        );
                    })}
                {!isLoading && showItineraries && itineraries.length === 0 && (
                    <div className="trpr-atlas-card__itineraries-loading">No itineraries</div>
                )}
            </div>
            {children}
        </article>
    );
}
AtlasCard.defaultProps = {
    onFavoriteBtnClick: () => {},
    onMapBtnClick: () => {},
    showOrder: true,
    itinerariesUrls: [],
};

export default AtlasCard;
